import React, { useState, useEffect } from 'react';
import { useToast } from '../../../Toaster/ToastContext'
import PropTypes from 'prop-types';
import { apiClient } from '../../../services/apiClient';

export default function AddCategoryList({ category, onSave }) {

    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        status: 'Active',
    });
    const [permissions, setPermissions] = useState({
        create: false,
        read: false,
        update: false,
        delete: false,
    });

    useEffect(() => {
        const storedPermissions = localStorage.getItem("rolespermissions");
        let parsedPermissions = [];
        try {
            const Permissions = JSON.parse(storedPermissions);
            const getPermissions = JSON.parse(Permissions.permissions)
            if(getPermissions.permissions){
                parsedPermissions = getPermissions.permissions;
            }
            else {
                parsedPermissions = getPermissions;
            }
        
            const inventoryPermissions = parsedPermissions.find(
                (item) => item.title === "Inventory"
            );

            const productPermissions = inventoryPermissions?.childPage?.find(
                (child) => child.title === "Category"
            );
            // console.log("search product", productPermissions)

            if (productPermissions) {
                setPermissions({
                    create: productPermissions.create || false,
                    read: productPermissions.read || false,
                    update: productPermissions.update || false,
                    delete: productPermissions.delete || false,
                });
            } else {
                console.error("Product permissions not found in localStorage");
                showToast("Product permissions not found.", "error", "Permissions Error");
            }
        } catch (error) {
            console.error("Error parsing permissions from localStorage:", error);
            showToast("Failed to load permissions.", "error", "Permissions Error");
        }
    }, []);

    useEffect(() => {
        if (category) {
            setFormData({
                id: category.id,
                name: category.name || '',
                slug: category.slug || '',
                status: category.status || 'Active',
            });
        } else {
            setFormData({ name: '', slug: '', status: 'Active' });
        }
        setErrors({ name: '', slug: '', status: '' });
    }, [category]);

    const [errors, setErrors] = useState({
        name: '',
        slug: '',
        status: '',
    });

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                return value.trim() ? '' : 'Category Name is required';
            case 'slug':
                return value.trim() ? '' : 'Category Slug is required';
            case 'status':
                return value ? '' : 'Status is required';
            default:
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    };

    const generateRandomSlug = () => {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        const slugLength = 8;
        let result = '';
        for (let i = 0; i < slugLength; i++) {
            const randomIndex = Math.floor(Math.random() * characters.length);
            result += characters[randomIndex];
        }
        setFormData((prev) => ({ ...prev, slug: result }));
        setErrors((prev) => ({ ...prev, slug: validateField('slug', result) }));
    };

    const validateForm = () => {
        const newErrors = {
            name: validateField('name', formData.name),
            slug: validateField('slug', formData.slug),
            status: validateField('status', formData.status),
        };
        setErrors(newErrors);
        return !Object.values(newErrors).some((error) => error !== '');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setLoading(true)
            try {
                const response = await apiClient.post('/store-category', formData);
                const Apidata = await response;
                if (Apidata.success) {
                    // Reset form
                    setFormData({ name: '', slug: '', status: 'Active' });
                    setErrors({ name: '', slug: '', status: '' });
                    document.getElementById('add-category').querySelector('[data-bs-dismiss="modal"]').click()
                    
                    // Show toast and call onSave
                    showToast(Apidata.message, 'success', 'Category Operation!');
                    onSave && onSave();
                }
            }
            catch (error) {
                showToast(error.message || 'An unexpected error occurred: Please contact the support team.', 'error', 'Category Operation!');
            }
            finally {
                setLoading(false)
            }
        }
    };

    return (
        <div>
            {/* Add Category */}
            <div className="modal fade" id="add-category">
                <div className="modal-dialog modal-dialog-centered custom-modal-two">
                    <div className="modal-content">
                        <div className="page-wrapper-new p-0">
                            <div className="content">
                                <div className="modal-header border-0 custom-modal-header d-flex justify-content-between align-items-center">
                                    <div className="page-title">
                                        <h4>{category ? 'Update Category' : 'Create Category'}</h4>
                                    </div>
                                    <button
                                        type="button"
                                        className="close"
                                        data-bs-dismiss="modal"
                                        aria-label="Close"
                                    >
                                        <span aria-hidden="true">×</span>
                                    </button>
                                </div>
                                <div className="modal-body custom-modal-body">
                                    <form onSubmit={handleSubmit}>
                                        <div className="mb-3">
                                            <label className="form-label">Category</label>
                                            <input 
                                                type="text" 
                                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                            />
                                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Category Slug</label>
                                            <div className="d-flex align-items-center">
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.slug ? 'is-invalid' : ''} me-2`}
                                                    name="slug"
                                                    value={formData.slug}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter category slug"
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary fs-13 fw-medium p-2 px-3"
                                                    onClick={generateRandomSlug}
                                                >
                                                    Random
                                                </button>
                                            </div>
                                            {errors.slug && <div className="invalid-feedback">{errors.slug}</div>}
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className={`form-select ${errors.status ? 'is-invalid' : ''}`}
                                                name="status"
                                                value={formData.status}
                                                onChange={handleInputChange}
                                            >
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                            {errors.status && <div className="invalid-feedback">{errors.status}</div>}
                                        </div>
                                        <div className="modal-footer-btn">
                                            <button
                                                type="button"
                                                className="btn btn-cancel me-2"
                                                data-bs-dismiss="modal"
                                            >
                                                Cancel
                                            </button>
                                            {(category ? permissions.update : permissions.create) && (
                                                <button type="submit" className="btn btn-submit" disabled={loading}>
                                                    {loading ? 'Processing...' : (category ? 'Update Category' : 'Create Category')}
                                                </button>
                                            )}
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* /Add Category */}
        </div>
    )
}

AddCategoryList.propTypes = {
    category: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        slug: PropTypes.string,
        status: PropTypes.oneOf(['active', 'inactive', '']),
    }),
    onSave: PropTypes.func,
};

AddCategoryList.defaultProps = {
    category: null,
};
